import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    User,
    Calendar,
    MapPin,
    Monitor,
    Key,
    Activity,
    FileText,
    ArrowRight,
    Clock,
    Shield,
    Database,
    Globe,
} from 'lucide-react';
import { AuditLog } from '@/models/audit';
import DateTimeFormatter from '@/components/shared/DateTimeFormatter';

interface AuditLogDetailsDialogProps {
    isOpen: boolean;
    onClose: () => void;
    auditLog: AuditLog | null;
}

const AuditLogDetailsDialog: React.FC<AuditLogDetailsDialogProps> = ({
    isOpen,
    onClose,
    auditLog,
}) => {
    if (!auditLog) return null;

    const getEventColor = (event: string) => {
        const eventLower = event.toLowerCase();
        if (eventLower.includes('create') || eventLower.includes('add')) {
            return 'bg-green-100 text-green-800 border-green-300';
        } else if (eventLower.includes('update') || eventLower.includes('edit')) {
            return 'bg-blue-100 text-blue-800 border-blue-300';
        } else if (eventLower.includes('delete') || eventLower.includes('remove')) {
            return 'bg-red-100 text-red-800 border-red-300';
        } else if (eventLower.includes('login') || eventLower.includes('auth')) {
            return 'bg-purple-100 text-purple-800 border-purple-300';
        }
        return 'bg-gray-100 text-gray-800 border-gray-300';
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            timeZoneName: 'short',
        });
    };

    const formatJsonValue = (value: string | null) => {
        if (!value) return null;
        try {
            const parsed = JSON.parse(value);
            return JSON.stringify(parsed, null, 2);
        } catch {
            return value;
        }
    };

    const renderValueComparison = () => {
        if (!auditLog.value_before && !auditLog.value_after) {
            return (
                <div className="text-sm text-muted-foreground italic">
                    No value changes recorded
                </div>
            );
        }

        return (
            <div className="space-y-4">
                {auditLog.value_before && (
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                            <span className="text-sm font-medium text-red-700">Before</span>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                            <pre className="text-xs text-red-800 whitespace-pre-wrap break-words">
                                {formatJsonValue(auditLog.value_before)}
                            </pre>
                        </div>
                    </div>
                )}

                {auditLog.value_before && auditLog.value_after && (
                    <div className="flex justify-center">
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                )}

                {auditLog.value_after && (
                    <div className="space-y-2">
                        <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-sm font-medium text-green-700">After</span>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <pre className="text-xs text-green-800 whitespace-pre-wrap break-words">
                                {formatJsonValue(auditLog.value_after)}
                            </pre>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const renderMetadata = () => {
        if (!auditLog.metadata || Object.keys(auditLog.metadata).length === 0) {
            return (
                <div className="text-sm text-muted-foreground italic">No additional metadata</div>
            );
        }

        return (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words">
                    {JSON.stringify(auditLog.metadata, null, 2)}
                </pre>
            </div>
        );
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-3">
                        <Activity className="h-5 w-5" />
                        Audit Log Details
                        <Badge variant="outline" className={getEventColor(auditLog.type)}>
                            {auditLog.type}
                        </Badge>
                    </DialogTitle>
                </DialogHeader>

                <ScrollArea className="max-h-[calc(90vh-120px)] pr-4">
                    <div className="space-y-6">
                        {/* Basic Information */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Database className="h-4 w-4" />
                                    Basic Information
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">
                                            Log ID
                                        </label>
                                        <div className="font-mono text-sm">{auditLog.id}</div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">
                                            Event Type
                                        </label>
                                        <div>
                                            <Badge
                                                variant="outline"
                                                className={getEventColor(auditLog.type)}
                                            >
                                                {auditLog.type}
                                            </Badge>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">
                                            Item Type
                                        </label>
                                        <div className="text-sm">{auditLog.item_type || 'N/A'}</div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">
                                            Item ID
                                        </label>
                                        <div className="font-mono text-sm">
                                            {auditLog.item_id || 'N/A'}
                                        </div>
                                    </div>
                                </div>

                                {auditLog.amount && (
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">
                                            Amount
                                        </label>
                                        <div className="text-sm font-medium">
                                            {auditLog.amount} {auditLog.currency || 'USD'}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* User Information */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <User className="h-4 w-4" />
                                    User Information
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">
                                            User Name
                                        </label>
                                        <div className="text-sm">
                                            {auditLog.user?.name || auditLog.created_by || 'System'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">
                                            User Email
                                        </label>
                                        <div className="text-sm">
                                            {auditLog.user?.email || auditLog.user_id || 'N/A'}
                                        </div>
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">
                                            User ID
                                        </label>
                                        <div className="font-mono text-sm">
                                            {auditLog.user_id || 'N/A'}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">
                                            Created By
                                        </label>
                                        <div className="text-sm">
                                            {auditLog.created_by || 'N/A'}
                                        </div>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Customer Information */}
                        {auditLog.customer_id && (
                            <Card>
                                <CardHeader className="pb-3">
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <FileText className="h-4 w-4" />
                                        Affected Customer
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-sm font-medium text-muted-foreground">
                                                Customer Name
                                            </label>
                                            <div className="text-sm font-medium">
                                                {auditLog.customer_name || 'N/A'}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-muted-foreground">
                                                Customer ID
                                            </label>
                                            <div className="font-mono text-sm">
                                                {auditLog.customer_id}
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* Value Changes */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <ArrowRight className="h-4 w-4" />
                                    Value Changes
                                </CardTitle>
                            </CardHeader>
                            <CardContent>{renderValueComparison()}</CardContent>
                        </Card>

                        {/* Technical Details */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Shield className="h-4 w-4" />
                                    Technical Details
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                                            <Calendar className="h-3 w-3" />
                                            Timestamp
                                        </label>
                                        <div className="text-sm">
                                            {formatDate(auditLog.created_at)}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                                            <Clock className="h-3 w-3" />
                                            Time Ago
                                        </label>
                                        <div className="text-sm">
                                            <DateTimeFormatter date={auditLog.created_at} />
                                        </div>
                                    </div>
                                </div>

                                {auditLog.ip_address && (
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                                            <MapPin className="h-3 w-3" />
                                            IP Address
                                        </label>
                                        <div className="font-mono text-sm">
                                            {auditLog.ip_address}
                                        </div>
                                    </div>
                                )}

                                {auditLog.user_agent && (
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                                            <Monitor className="h-3 w-3" />
                                            User Agent
                                        </label>
                                        <div className="text-xs bg-gray-100 p-2 rounded border break-all">
                                            {auditLog.user_agent}
                                        </div>
                                    </div>
                                )}

                                {auditLog.api_key_id && (
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground flex items-center gap-1">
                                            <Key className="h-3 w-3" />
                                            API Key ID
                                        </label>
                                        <div className="font-mono text-sm">
                                            {auditLog.api_key_id}
                                        </div>
                                    </div>
                                )}
                            </CardContent>
                        </Card>

                        {/* Metadata */}
                        <Card>
                            <CardHeader className="pb-3">
                                <CardTitle className="text-lg flex items-center gap-2">
                                    <Globe className="h-4 w-4" />
                                    Additional Metadata
                                </CardTitle>
                            </CardHeader>
                            <CardContent>{renderMetadata()}</CardContent>
                        </Card>
                    </div>
                </ScrollArea>
            </DialogContent>
        </Dialog>
    );
};

export default AuditLogDetailsDialog;
